import React, { useEffect, useMemo, useState } from 'react';
import { hasWechatOpenTagConfig, miniProgramConfig } from '../../config/miniProgram';
import { fetchWechatSignature, isWechatBrowser, loadWechatSdk } from '../../utils/wechat';

type Props = {
  label: string;
  style?: React.CSSProperties;
  fullWidth?: boolean;
  withArrow?: boolean;
};

function toKebabCase(key: string) {
  return key.replace(/[A-Z]/g, match => `-${match.toLowerCase()}`);
}

function toCssText(style: React.CSSProperties) {
  return Object.entries(style)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([key, value]) => `${toKebabCase(key)}:${String(value)}`)
    .join(';');
}

const MiniProgramLaunchButton: React.FC<Props> = ({
  label,
  style,
  fullWidth = false,
  withArrow = false,
}) => {
  const [wechatReady, setWechatReady] = useState(false);
  const [wechatFailed, setWechatFailed] = useState(false);

  const wechat = useMemo(() => isWechatBrowser(), []);
  const baseStyle = useMemo<React.CSSProperties>(() => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    textDecoration: 'none',
    cursor: 'pointer',
    width: fullWidth ? '100%' : undefined,
    ...style,
  }), [fullWidth, style]);

  useEffect(() => {
    if (!wechat || !hasWechatOpenTagConfig()) return;

    let cancelled = false;

    const bootstrap = async () => {
      try {
        const wx = await loadWechatSdk();
        if (!wx) throw new Error('WeChat JS SDK missing');

        const signature = await fetchWechatSignature(miniProgramConfig.jsSdkSignatureUrl);
        if (cancelled) return;

        wx.config({
          debug: false,
          appId: signature.appId,
          timestamp: signature.timestamp,
          nonceStr: signature.nonceStr,
          signature: signature.signature,
          jsApiList: ['checkJsApi'],
          openTagList: ['wx-open-launch-weapp'],
        });

        wx.ready(() => {
          if (!cancelled) setWechatReady(true);
        });

        wx.error(() => {
          if (!cancelled) setWechatFailed(true);
        });
      } catch (_error) {
        if (!cancelled) setWechatFailed(true);
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [wechat]);

  const fallbackHref = miniProgramConfig.urlScheme || miniProgramConfig.qrFallbackHash;
  const content = (
    <>
      <span>{label}</span>
      {withArrow && <span aria-hidden="true">→</span>}
    </>
  );

  if (wechat && hasWechatOpenTagConfig() && wechatReady && !wechatFailed) {
    const templateStyle = toCssText(baseStyle);

    return React.createElement(
      'wx-open-launch-weapp',
      {
        username: miniProgramConfig.username,
        path: miniProgramConfig.path,
        style: { display: fullWidth ? 'block' : 'inline-block', width: fullWidth ? '100%' : undefined },
      },
      React.createElement('script', {
        type: 'text/wxtag-template',
        dangerouslySetInnerHTML: {
          __html: `<div style="${templateStyle}">${label}${withArrow ? '<span aria-hidden="true">→</span>' : ''}</div>`,
        },
      })
    );
  }

  return (
    <a href={fallbackHref} style={baseStyle}>
      {content}
    </a>
  );
};

export default MiniProgramLaunchButton;
